import { validationEnabled } from './questionSettings';
import { temporalError } from './temporal';
import type { CustomAnswer, Field, FieldValidation } from './types';

export function isInputField(f: Field): boolean {
  return f.type !== 'section' && f.type !== 'title_text';
}

export const fieldDescription = (f: Field) => f.descriptionEnabled === false || !f.help?.trim() ? undefined : f.help;

export const DEFAULT_ACKNOWLEDGMENT_LABEL = 'I agree';
export function acknowledgmentLabel(f: Field): string {
  return f.acknowledgmentLabel?.trim() || DEFAULT_ACKNOWLEDGMENT_LABEL;
}

/** Bind acknowledgment to both the statement and checkbox wording the participant saw. */
export function acknowledgmentValue(f: Field): string {
  const value = ['acknowledgment', f.label, fieldDescription(f) ?? ''];
  const label = acknowledgmentLabel(f);
  // Preserve existing acknowledgments when the default wording has not changed.
  if (label !== DEFAULT_ACKNOWLEDGMENT_LABEL) value.push(label);
  return JSON.stringify(value);
}

function runValidation(v: FieldValidation, value: string): string | null {
  const msg = v.message?.trim();
  const fail = (d: string) => msg || d;
  if (v.kind === 'number') {
    const n = Number(value);
    if (value.trim() === '' || !Number.isFinite(n)) return fail('Must be a number');
    const a = Number(v.value), b = Number(v.value2);
    switch (v.op) {
      case 'gt': return n > a ? null : fail(`Must be greater than ${a}`);
      case 'gte': return n >= a ? null : fail(`Must be at least ${a}`);
      case 'lt': return n < a ? null : fail(`Must be less than ${a}`);
      case 'lte': return n <= a ? null : fail(`Must be at most ${a}`);
      case 'eq': return n === a ? null : fail(`Must equal ${a}`);
      case 'neq': return n !== a ? null : fail(`Must not equal ${a}`);
      case 'between': return n >= a && n <= b ? null : fail(`Must be between ${a} and ${b}`);
      case 'not_between': return n < a || n > b ? null : fail(`Must not be between ${a} and ${b}`);
      case 'is_number': return null;
      case 'integer': return Number.isInteger(n) ? null : fail('Must be a whole number');
      default: return null;
    }
  }
  if (v.kind === 'text') {
    const needle = String(v.value);
    switch (v.op) {
      case 'contains': return value.includes(needle) ? null : fail(`Must contain "${needle}"`);
      case 'not_contains': return !value.includes(needle) ? null : fail(`Must not contain "${needle}"`);
      case 'email': return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? null : fail('Must be a valid email address');
      case 'url': try { new URL(value); return null; } catch { return fail('Must be a valid URL'); }
      default: return null;
    }
  }
  if (v.kind === 'length') {
    const n = Number(v.value);
    if (v.op === 'max') return value.length <= n ? null : fail(`Maximum ${n} characters`);
    if (v.op === 'min') return value.length >= n ? null : fail(`Minimum ${n} characters`);
    return null;
  }
  if (v.kind === 'regex') {
    let re: RegExp;
    try { re = new RegExp(String(v.value)); } catch { return null; }
    const hit = re.test(value);
    const fullMatch = new RegExp(`^(?:${String(v.value)})(?![\\s\\S])`).test(value);
    switch (v.op) {
      case 'contains': return hit ? null : fail('Invalid format');
      case 'not_contains': return !hit ? null : fail('Invalid format');
      case 'matches': return fullMatch ? null : fail('Invalid format');
      case 'not_matches': return !fullMatch ? null : fail('Invalid format');
      default: return null;
    }
  }
  return null;
}

export function isEmptyAnswer(f: Field, a: CustomAnswer | undefined): boolean {
  if (a === undefined || a === null) return true;
  if (typeof a === 'string') return a.trim() === '';
  if (Array.isArray(a)) return a.length === 0;
  if (f.type === 'choice_grid' || f.type === 'checkbox_grid') {
    const rows = f.grid?.rows ?? [];
    return rows.some((r) => { const v = a[r]; return v === undefined || (Array.isArray(v) ? v.length === 0 : String(v) === ''); });
  }
  return Object.keys(a).length === 0;
}

/** Returns an error message, or null if the answer is acceptable. */
export function validateAnswer(f: Field, a: CustomAnswer | undefined): string | null {
  if (!isInputField(f)) return null;
  if (f.type === 'acknowledgment') {
    if (a === acknowledgmentValue(f)) return null;
    const label = acknowledgmentLabel(f);
    return label === DEFAULT_ACKNOWLEDGMENT_LABEL ? 'Please agree to this statement before continuing.' : `Please check “${label}” before continuing.`;
  }
  if (f.type === 'rating' && a !== undefined && a !== null && a !== '' && (typeof a !== 'string' || !/^(?:[1-9]|10)$/.test(a) || Number(a) > (f.rating?.max ?? 5))) return `Choose a rating from 1 to ${f.rating?.max ?? 5}.`;
  if ((f.type === 'choice' || f.type === 'dropdown') && f.routing?.enabled && a !== undefined && a !== '') {
    if (typeof a !== 'string' || (!(f.options ?? []).includes(a) && !(f.type === 'choice' && f.allowOther && a.trim()))) return 'Choose one of the available answers.';
  }
  if (f.type === 'checkboxes' && a !== undefined && a !== null) {
    if (!Array.isArray(a) || a.some(v => typeof v !== 'string' || !v.trim()) || new Set(a).size !== a.length || a.filter(v => !f.options?.includes(v)).length > (f.allowOther ? 1 : 0)) return 'Choose from the available options.';
    if (a.length && validationEnabled(f) && f.validation?.kind === 'selection') {
      const count = Number(f.validation.value), op = f.validation.op;
      if ((op === 'min' && a.length < count) || (op === 'max' && a.length > count) || (op === 'exact' && a.length !== count)) return f.validation.message?.trim() || `Select ${op === 'min' ? 'at least' : op === 'max' ? 'at most' : 'exactly'} ${count} option${count === 1 ? '' : 's'}.`;
    }
  }
  if ((f.type === 'choice_grid' || f.type === 'checkbox_grid') && a !== undefined && a !== null) {
    if (typeof a !== 'object' || Array.isArray(a)) return 'Choose from the available grid options.';
    const selected: string[] = [];
    for (const [row, answer] of Object.entries(a)) {
      if (!f.grid?.rows.includes(row) || (f.type === 'choice_grid' ? typeof answer !== 'string' : !Array.isArray(answer))) return 'Choose from the available grid options.';
      const columns = Array.isArray(answer) ? answer : answer ? [answer] : [];
      if (columns.some(column => !f.grid?.columns.includes(column)) || new Set(columns).size !== columns.length) return 'Choose from the available grid options.';
      selected.push(...columns);
    }
    if (f.limitOnePerColumn && new Set(selected).size !== selected.length) return 'Choose no more than one response per column.';
  }
  if ((f.type === 'date' || f.type === 'time') && a !== undefined && a !== null && a !== '') {
    if (typeof a !== 'string') return 'Enter a valid date or time.';
    const error = temporalError(f, a); if (error) return error;
  }
  const empty = isEmptyAnswer(f, a);
  if (empty) return f.required ? f.type === 'short_text' ? 'Enter an answer.' : 'This question is required' : null;
  if ((f.type === 'short_text' || f.type === 'paragraph') && f.validation && typeof a === 'string') {
    return validationEnabled(f) ? runValidation(f.validation, a) : null;
  }
  return null;
}

export function answerToText(f: Field, a: CustomAnswer | undefined): string {
  if (f.type === 'acknowledgment') {
    if (a !== acknowledgmentValue(f)) return '';
    const label = acknowledgmentLabel(f);
    return label === DEFAULT_ACKNOWLEDGMENT_LABEL ? 'Agreed' : label;
  }
  if (a === undefined || a === null) return '';
  if (f.type === 'rating' && typeof a === 'string' && a) return `${a} / ${f.rating?.max ?? 5}`;
  if (typeof a === 'string') return a;
  if (Array.isArray(a)) return a.join('; ');
  return Object.entries(a).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join('; ') : v}`).join('\n');
}
